/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import { MRT_TableInstance, MRT_RowVirtualizer } from 'mantine-react-table';
import {
  ResourceRowFragmentFragment,
  ResourceType,
  ListResourcesQueryVariables,
} from '@app-types/blackroom-api';
import { useReactiveVar } from '@apollo/client';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { ResourceScope } from '@app-types/blackroom-app';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import {
  clearCopiedResourceIds,
  resourceClipboardVar,
  setClipboardResources,
} from '@features/Resource/store/copiedResources';
import { useResourceActions } from '@app/shared/helpers/file';

const isVisibleElement = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    element.getClientRects().length > 0
  );
};

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

interface UseResourceTableKeyboardNavigationProps {
  table: MRT_TableInstance<ResourceRowFragmentFragment> | null;
  rowVirtualizerInstanceRef?: React.RefObject<MRT_RowVirtualizer>;
  onNavigateToFolder?: (folderId: string) => void;
  onNavigateBack?: () => void;
  canNavigateBack?: boolean;
  currentFolderId?: string | null;
  scope?: ResourceScope;
  requestVariables: ListResourcesQueryVariables;
}

interface UseResourceTableKeyboardNavigationReturn {
  handleRowClick: (resourceId: string, event?: React.MouseEvent) => void;
  isResourceFlashing: (resourceId: string) => boolean;
  clearCopiedResources: () => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

export function useResourceTableKeyboardNavigation({
  table,
  rowVirtualizerInstanceRef,
  onNavigateToFolder,
  onNavigateBack,
  canNavigateBack = false,
  currentFolderId = null,
  scope = 'my',
  requestVariables,
}: UseResourceTableKeyboardNavigationProps): UseResourceTableKeyboardNavigationReturn {
  /* ********************************** HOOKS ********************************* */

  const dialogStore = useReactiveVar(dialogStoreVar);
  const { delete: deleteResourcesAction } = useResourceActions();

  // Use the shared reactive variable for copied resources
  const resourceClipboard = useReactiveVar(resourceClipboardVar);

  const { t } = useTranslation(['Resource']);

  /* ********************************** STATE ********************************* */

  const [flashingResourceIds, setFlashingResourceIds] = useState<string[]>([]);
  const [lastNavigationEdge, setLastNavigationEdge] = useState<
    'top' | 'bottom' | null
  >(null);

  /* ****************************** SIDE-EFFECTS ****************************** */

  // Handle keyboard shortcuts and navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore synthetic or malformed events before reading keyboard properties.
      if (!event.isTrusted || typeof event.key !== 'string') {
        return;
      }

      const key = event.key.toLowerCase();
      const isModifierShortcut = event.metaKey || event.ctrlKey;
      const isSelectAllShortcut = isModifierShortcut && key === 'a' && !!table;

      // Check if we're focused on an input element (but allow checkboxes)
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused =
        (activeElement?.tagName === 'INPUT' &&
          (activeElement as HTMLInputElement).type !== 'checkbox') ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.contentEditable === 'true';

      // Check if a modal is currently open and visible
      const isModalOpen = Array.from(
        document.querySelectorAll<HTMLElement>('[role="dialog"]'),
      ).some(isVisibleElement);

      if (isInputFocused || !table || isModalOpen) {
        return;
      }

      const rows = table.getRowModel().rows;

      // Handle cmd+c / ctrl+c for copying
      const isCopyShortcut = isModifierShortcut && key === 'c';

      if (isCopyShortcut) {
        event.preventDefault();

        // Get selected rows from the table
        const selectedRows = table.getSelectedRowModel().rows;
        const resourcesToCopy = selectedRows.map((row) => row.original.id);

        if (resourcesToCopy.length > 0) {
          // Replace clipboard with new selection (persistent, for paste logic)
          setClipboardResources(resourcesToCopy, 'copy');

          // Add to flashing list for visual feedback
          setFlashingResourceIds(resourcesToCopy);

          // Show notification
          showNotification({
            title: t('Resource:Common.notifications.resourcesCopied.title', {
              count: resourcesToCopy.length,
            }),
            message: t('Resource:Common.notifications.resourcesCopied.message'),
            color: 'blackroomBlue',
          });

          // Remove the visual flash effect after 100ms
          setTimeout(() => {
            setFlashingResourceIds([]);
          }, 100);
        }
      }

      // Handle cmd+x / ctrl+x for cutting
      const isCutShortcut = isModifierShortcut && key === 'x';

      if (isCutShortcut) {
        event.preventDefault();

        // Get selected rows from the table
        const selectedRows = table.getSelectedRowModel().rows;
        const resourcesToCut = selectedRows.map((row) => row.original.id);

        if (resourcesToCut.length > 0) {
          // Replace clipboard with new selection (persistent, for paste logic)
          setClipboardResources(resourcesToCut, 'cut');

          // Add to flashing list for visual feedback
          setFlashingResourceIds(resourcesToCut);

          // Show notification
          showNotification({
            title: t('Resource:Common.notifications.resourcesCut.title', {
              count: resourcesToCut.length,
            }),
            message: t('Resource:Common.notifications.resourcesCut.message'),
            color: 'blackroomBlue',
          });

          // Remove the visual flash effect after 100ms
          setTimeout(() => {
            setFlashingResourceIds([]);
          }, 100);
        }
      }

      // Handle cmd+v / ctrl+v for pasting
      const isPasteShortcut = isModifierShortcut && key === 'v';

      if (isPasteShortcut) {
        event.preventDefault();

        // Only paste if there are copied resources
        if (resourceClipboard.resourceIds.length > 0) {
          handlePaste(currentFolderId);
        }
      }

      // Handle arrow key navigation
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        // Only allow navigation if there are rows
        if (rows.length === 0) {
          return;
        }

        event.preventDefault();

        // Get currently selected rows
        const selectedRows = table.getSelectedRowModel().rows;

        // Determine which edge to navigate from
        let currentIndex = -1;
        if (selectedRows.length > 0) {
          let edgeSelectedId: string;

          if (event.altKey) {
            // When holding Alt (multi-select mode), pick edge based on arrow direction
            // and track which edge we're at
            if (event.key === 'ArrowDown') {
              edgeSelectedId =
                selectedRows[selectedRows.length - 1].original.id;
              setLastNavigationEdge('bottom');
            } else {
              edgeSelectedId = selectedRows[0].original.id;
              setLastNavigationEdge('top');
            }
          } else {
            // When NOT holding Alt (exiting multi-select), continue from the last edge
            if (lastNavigationEdge === 'bottom') {
              edgeSelectedId =
                selectedRows[selectedRows.length - 1].original.id;
            } else if (lastNavigationEdge === 'top') {
              edgeSelectedId = selectedRows[0].original.id;
            } else {
              // No previous edge tracked, use direction-based logic
              edgeSelectedId =
                event.key === 'ArrowDown'
                  ? selectedRows[selectedRows.length - 1].original.id
                  : selectedRows[0].original.id;
              setLastNavigationEdge(
                event.key === 'ArrowDown' ? 'bottom' : 'top',
              );
            }
          }

          currentIndex = rows.findIndex(
            (row) => row.original.id === edgeSelectedId,
          );
        }

        // If no row is selected, start at the beginning
        if (currentIndex === -1) {
          currentIndex = 0;
          setLastNavigationEdge('bottom');
        } else {
          // Navigate up or down
          if (event.key === 'ArrowDown') {
            currentIndex = Math.min(currentIndex + 1, rows.length - 1);
            if (!event.altKey) setLastNavigationEdge('bottom');
          } else if (event.key === 'ArrowUp') {
            currentIndex = Math.max(currentIndex - 1, 0);
            if (!event.altKey) setLastNavigationEdge('top');
          }
        }

        // Get the new resource to select
        const newResource = rows[currentIndex]?.original;
        if (newResource) {
          // If Alt key is pressed, add to multi-selection
          if (event.altKey) {
            const newRow = table.getRow(newResource.id);
            if (newRow && !newRow.getIsSelected()) {
              newRow.toggleSelected();
            }
          } else {
            // Normal navigation: select only this resource (single selection)
            table.setRowSelection({ [newResource.id]: true });
          }

          // Scroll to the selected row to make it visible
          if (rowVirtualizerInstanceRef?.current) {
            try {
              rowVirtualizerInstanceRef.current.scrollToIndex(currentIndex, {
                align: 'center',
              });
            } catch (e) {
              console.error('Failed to scroll to row:', e);
            }
          }
        }
      }

      // Handle Escape key to clear selection
      if (event.key === 'Escape') {
        table.resetRowSelection();
        setLastNavigationEdge(null);
      }

      // Handle cmd+a / ctrl+a for select all
      if (isSelectAllShortcut) {
        // Only works if there are rows to select
        if (rows.length === 0) {
          return;
        }

        event.preventDefault();
        table.toggleAllRowsSelected(true);
      }

      // Handle Backspace key variants
      if (key === 'backspace') {
        // cmd+backspace / ctrl+backspace for delete
        if (isModifierShortcut) {
          event.preventDefault();

          // Get selected rows from the table
          const selectedRows = table.getSelectedRowModel().rows;
          const resourcesToDelete = selectedRows.map((row) => row.original.id);

          if (resourcesToDelete.length > 0) {
            handleDelete();
          }
        }
        // Plain backspace to navigate back/up to parent folder
        else if (canNavigateBack && onNavigateBack) {
          event.preventDefault();
          onNavigateBack();
        }
      }

      // Handle Enter key to navigate into folders
      if (event.key === 'Enter') {
        // Only works if there are rows to select
        if (rows.length === 0) {
          return;
        }

        const selectedRows = table.getSelectedRowModel().rows;

        // Only navigate if exactly one row is selected
        if (selectedRows.length === 1 && onNavigateToFolder) {
          const selectedResource = selectedRows[0].original;

          // Check if it's a folder
          if (selectedResource.type === ResourceType.Folder) {
            event.preventDefault();
            onNavigateToFolder(selectedResource.id);
          }
        }
      }
    };

    // Add event listener to the document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    table,
    t,
    lastNavigationEdge,
    onNavigateToFolder,
    onNavigateBack,
    canNavigateBack,
    rowVirtualizerInstanceRef,
    resourceClipboard,
    currentFolderId,
    scope,
  ]);

  /* ******************************** CALLBACKS ******************************* */

  const handlePaste = (destinationFolderId: string | null) => {
    dialogStore?.invokeDialog?.('dialog-resource-copy', {
      destinationFolderId,
      requestVariables,
      modalProps: {
        withCloseButton: false,
      },
    });
  };

  const handleDelete = () => {
    if (!table) return;

    deleteResourcesAction.action({
      resources: table.getSelectedRowModel().rows.map((row) => row.original),
    });
  };

  const handleRowClick = (resourceId: string, event?: React.MouseEvent) => {
    if (!table) return;

    // If Alt/Option key is pressed, toggle selection (multi-select mode)
    if (event?.altKey) {
      const row = table.getRow(resourceId);
      if (row) {
        row.toggleSelected();
      }
      // Don't reset navigation edge in multi-select mode
    } else {
      // Normal click: select only this resource (single selection)
      table.setRowSelection({ [resourceId]: true });
      // Reset navigation edge on new single selection
      setLastNavigationEdge(null);
    }
  };

  const isResourceFlashing = (resourceId: string) => {
    return flashingResourceIds.includes(resourceId);
  };

  const clearCopiedResources = () => {
    clearCopiedResourceIds();
  };

  /* ********************************** RETURN ******************************** */

  return {
    handleRowClick,
    isResourceFlashing,
    clearCopiedResources,
  };
}
